// Bundled into client builds via @tn/configs; Next's webpack can't resolve `node:` URIs.
// oxlint-disable-next-line unicorn/prefer-node-protocol
import { promises } from 'fs';

import { resolvePath } from './resolvePath';
import { Configuration, InstanceData } from './types';

type Ctx = {
    instanceData: InstanceData;
};

type Params = {
    region: string;
    lob: string;
    configFileOverride?: string;
};

type ReturnedValue = Promise<Configuration | undefined>;

// Shared FI MFE pods serve both LOBs, so a pod's process.env host would bleed into the other
// LOB's config; only cross-LOB loads (or pods without a LOB) keep the checked-in blueprint host.
const FI_HOST_KEYS = new Set(['API_HOST', 'API_CONTENT_HOST', 'API_CONTENT_VOD_HOST']);
const isCrossLobFeatureInstanceLoad = (lob: string): boolean =>
    process.env.CURRENT_ENV === 'platypus' &&
    Boolean(process.env.SUBDOMAIN?.trim()) &&
    process.env.LOB?.trim().toLowerCase() !== lob.toLowerCase();

export const loadConfig =
    ({ instanceData }: Ctx) =>
    async ({ region, lob, configFileOverride }: Params): ReturnedValue => {
        const configFilePath = resolvePath({ instanceData })({
            region: region.toLowerCase(),
            lob: lob.toLowerCase(),
            configFileOverride,
        });
        try {
            const fileContent = await promises.readFile(configFilePath, { encoding: 'utf-8' });
            const fileContentJSON = JSON.parse(fileContent);

            // env.* is a special case and should always come from process.env if defined
            if (fileContentJSON.env) {
                const skipHostOverride = isCrossLobFeatureInstanceLoad(lob);
                Object.entries(fileContentJSON.env).forEach(([key, value]) => {
                    if (skipHostOverride && FI_HOST_KEYS.has(key)) {
                        return;
                    }
                    fileContentJSON.env[key] = process.env[key] ?? value;
                });
            }

            return fileContentJSON as Configuration;
        } catch (error) {
            // Cannot use appLogger here: @tn/configs is a leaf package that
            // sits below @tn/foundation in the dependency graph (foundation
            // depends on configs, not the reverse). Importing appLogger would
            // create a cycle and violate the layer rule in
            // .ai-rules/01-monorepo/package-boundaries.md. console.error is
            // the documented fallback for infrastructure code.
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };
